//
// A row of action buttons that keeps its order: whatever does not fit collapses
// into a trailing menu instead of scrolling out of reach.
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useTownAssets } from '../../stores';
import {
  appTooltipProps,
} from '../../utils';
import SvgColorImg from '../SvgColorImg';

import Button from './Button';
import OvalButton from './OvalButton';

const gapPx = 16;

// How many of the buttons fit, keeping room for the menu button as soon as one
// of them has to move into it
const fittingCount = (widths, available, menuWidth) => {
  const total = widths.reduce((sum, width, index) => (sum + width + ((index) ? gapPx : 0)), 0);
  if (total <= available) return widths.length;

  const budget = available - menuWidth - gapPx;
  let used = 0;
  let count = 0;
  widths.forEach((width, index) => {
    if (count !== index) return;

    const next = used + width + ((index) ? gapPx : 0);
    if (next > budget) return;

    used = next;
    count = index + 1;
  });

  return count;
};

function ActionButtonsRow(props) {
  const {
    actions,
    className,
  } = props;
  const { assets } = useTownAssets();
  const { translate } = assets;
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(actions.length);
  const [menuPos, setMenuPos] = useState(null);

  // The off-screen copy is what gets measured: the visible row only holds the
  // buttons that fit, so it cannot answer how wide the others would be
  const actionsKey = actions.map((action) => (action.key)).join('|');

  useLayoutEffect(
    () => {
      const row = rowRef.current;
      const measure = measureRef.current;
      if ((!row) || (!measure)) return undefined;

      const fit = () => {
        const widths = Array.from(measure.children).map((child) => (child.offsetWidth));
        const menuWidth = menuButtonRef.current?.offsetWidth ?? 44;

        setVisibleCount(fittingCount(widths, row.clientWidth, menuWidth));
      };

      fit();

      const observer = new ResizeObserver(fit);
      observer.observe(row);

      return () => {
        observer.disconnect();
      };
    },
    [actionsKey],
  );

  const openMenu = useCallback(
    (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    },
    [],
  );

  const closeMenu = useCallback(
    () => {
      setMenuPos(null);
    },
    [],
  );

  useEffect(
    () => {
      if (!menuPos) return undefined;

      const onResize = () => {
        setMenuPos(null);
      };
      window.addEventListener('resize', onResize);

      return () => {
        window.removeEventListener('resize', onResize);
      };
    },
    [menuPos],
  );

  const renderButton = (action) => (
    <OvalButton
      key={action.key}
      name={action.key}
      onClick={action.onClick}
      disabled={action.disabled}
      label={action.label}
      imgSrc={action.imgSrc}
      imgRight={action.imgRight}
      color={action.color}
      tooltipTxt={action.tooltipTxt}
    />
  );

  const hidden = actions.slice(visibleCount);

  return (
    <div className={classNames('relative w-full min-w-0', className)}>
      <div
        ref={measureRef}
        aria-hidden
        className="absolute left-0 top-0 -z-10 flex flex-row gap-4 opacity-0 pointer-events-none w-max"
      >
        {actions.map(renderButton)}
      </div>

      <div
        ref={rowRef}
        className="flex flex-row items-center justify-center gap-4 w-full min-w-0 overflow-hidden"
      >
        {actions.slice(0, visibleCount).map(renderButton)}

        {(hidden.length)
          ? (
              <div ref={menuButtonRef} className="shrink-0">
                <Button
                  id="moreActions"
                  name="moreActions"
                  onClick={openMenu}
                  imgSrc="/images/svg/gui/dotdotdot.svg"
                  tooltipTxt={translate('otherActions')}
                  noFrame
                />
              </div>
            )
          : null}
      </div>

      {(menuPos && hidden.length)
        ? createPortal(
            <>
              <button
                type="button"
                aria-label="close"
                className="fixed inset-0 z-2000 bg-transparent cursor-default"
                onClick={closeMenu}
              />
              <div
                className="fixed z-2000 min-w-44 rounded-2xl bg-white shadow-xl py-2 flex flex-col"
                style={{ top: menuPos.top, right: menuPos.right }}
              >
                {hidden.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    name={action.key}
                    disabled={action.disabled}
                    {...appTooltipProps(action.tooltipTxt, 'left')}
                    onClick={() => {
                      closeMenu();
                      action.onClick();
                    }}
                    className={classNames(
                      'flex flex-row items-center justify-between gap-6 px-4 py-2 w-full text-start font-semibold',
                      {
                        'opacity-40 cursor-not-allowed': action.disabled,
                        'hover:bg-pxCaseHeaderSecondaryBg': !action.disabled,
                      },
                    )}
                  >
                    <span>{action.label}</span>
                    <SvgColorImg
                      src={action.imgSrc}
                      className="w-5 h-5 shrink-0 fill-pxPrimary"
                      imgColorAsCssFill
                    />
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}

ActionButtonsRow.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
    imgRight: PropTypes.bool,
    color: PropTypes.string,
    tooltipTxt: PropTypes.string,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
  })).isRequired,
  className: PropTypes.string,
};

export default ActionButtonsRow;
